import { useEffect, useRef } from 'react';

/**
 * Clears `inert` on this dialog's body-level portal in case a parent
 * modal's focus trap inerted it. Mirrors MADS `Dialog.web.tsx` (RN-4068).
 */
export function uninertOwnPortal(dialogEl: HTMLElement | null) {
    if (!dialogEl || typeof document === 'undefined') return;
    let bodyChild = dialogEl;
    while (bodyChild.parentElement && bodyChild.parentElement !== document.body) {
        bodyChild = bodyChild.parentElement;
    }
    if ((bodyChild as HTMLElement & { inert?: boolean }).inert) {
        (bodyChild as HTMLElement & { inert?: boolean }).inert = false;
    }
}

/**
 * Supplements React Native's Modal on web.
 *
 * On native, RNModal presents a separate OS-level screen that automatically
 * isolates focus, hides background content from assistive technology, and
 * restores focus on dismiss. On web the modal is a portal-rendered <div>,
 * so the browser has no built-in awareness of it. This hook fills that gap:
 *
 * 1. Moves focus into the modal heading (or the modal container) on open
 * 2. Traps keyboard focus so Tab cannot reach elements behind the backdrop
 * 3. Marks all DOM siblings as `inert` to hide them from screen readers
 * 4. Restores focus to the previously-focused element on close
 */
export function useModalFocusTrap(
    dialogRef: React.RefObject<object | null>,
    visible: boolean,
) {
    const previousFocusRef = useRef<Element | null>(null);

    useEffect(() => {
        if (!visible) return;

        const dialogEl = dialogRef.current as unknown as HTMLElement | null;
        previousFocusRef.current = document.activeElement;

        uninertOwnPortal(dialogEl);

        const heading = dialogEl?.querySelector?.('[role="heading"]') as HTMLElement | null;
        if (heading) {
            heading.tabIndex = -1;
            heading.focus();
        } else {
            dialogEl?.focus?.();
        }

        const handleFocusIn = (e: FocusEvent) => {
            if (dialogEl && !dialogEl.contains(e.target as Node)) {
                e.stopPropagation();
                dialogEl.focus();
            }
        };
        document.addEventListener('focusin', handleFocusIn);

        const inertedElements: HTMLElement[] = [];
        let node: HTMLElement | null = dialogEl?.parentElement ?? null;
        while (node && node !== document.body) {
            const parent: HTMLElement | null = node.parentElement;
            if (parent) {
                for (const sibling of Array.from(parent.children)) {
                    if (sibling === node) continue;
                    if (!(sibling as HTMLElement).inert) {
                        (sibling as HTMLElement).inert = true;
                        inertedElements.push(sibling as HTMLElement);
                    }
                }
            }
            node = parent;
        }

        return () => {
            document.removeEventListener('focusin', handleFocusIn);
            for (const el of inertedElements) {
                el.inert = false;
            }
            const prev = previousFocusRef.current as HTMLElement | null;
            if (prev && document.contains(prev)) {
                prev.focus?.();
            }
            previousFocusRef.current = null;
        };
    }, [visible, dialogRef]);
}

/** Closes the modal when the user presses Escape. */
export function useEscapeKey(
    onCloseRef: React.RefObject<(() => void) | null>,
    visible: boolean,
) {
    useEffect(() => {
        if (!visible) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onCloseRef.current?.();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [visible, onCloseRef]);
}
